import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';

/**
 * Source-of-truth URL query state. Returns [params, setParams].
 *
 * Use this instead of mirroring URL query into useState + useEffect.
 * The URL is the single source of truth; components derive their
 * values directly from `params` on every render and call `setParams`
 * inside event handlers to update the URL.
 *
 * @example
 *   const [query, setParams] = useUrlState();
 *   const search = query.search || '';
 *   const status = getStatusFromQuery(query.status);
 *
 *   const handleSearchChange = (value) =>
 *     setParams({ search: value || undefined });
 *
 * setParams accepts either an object of updates or a function
 * (current) => updates. Keys whose value is undefined, null, or '' are
 * removed from the URL.
 */
export default function useUrlState() {
  const { search } = useLocation();
  const history = useHistory();

  const params = queryString.parse(search, { parseBooleans: true });

  const setParams = useCallback(
    updates => {
      // Read from history.location (not the closed-over `search`) so
      // two synchronous setParams calls in the same handler compose.
      const current = queryString.parse(history.location.search, { parseBooleans: true });
      const resolved = typeof updates === 'function' ? updates(current) : updates;

      const next = { ...current };
      for (const [key, value] of Object.entries(resolved)) {
        if (value === undefined || value === null || value === '') {
          delete next[key];
        } else {
          next[key] = value;
        }
      }

      const nextSearch = queryString.stringify(next);
      const currentSearch = history.location.search.replace(/^\?/, '');
      if (nextSearch === currentSearch) return;

      history.replace({ search: nextSearch });
    },
    [history],
  );

  return [params, setParams];
}
